import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api-client';
import { invalidate } from '../../lib/cache';
import { Spinner } from '../../components/kit/Spinner';
import { Card } from '../../components/kit/Card';
import { Button } from '../../components/kit/Button';
import { useAuth, type DriverProfile } from '../../lib/auth';
import { useSetPageHeader } from '../../components/app/AppShell';
import { useTheme } from '../../lib/theme';
import { currentLanguage } from '../../lib/i18n';
import { LANG_LIST } from '../../lib/i18n/languages';
import { formatAadhaar } from '../../lib/aadhaar';
import { haptic } from '../../lib/haptic';
import { InstallCard } from '../../components/install/InstallCard';
import { PushStatusCard } from '../../components/profile/PushStatusCard';
import { env } from '../../lib/env';

// --- Inline SVG icons (no new dep) ---------------------------------------

function CameraIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
      <line x1="6" y1="15" x2="10" y2="15" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="text-[var(--color-text-secondary)] flex-shrink-0" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ArrowUp() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function FileWarnIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="12" y1="18" x2="12" y2="12" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  );
}

// --- Helpers -------------------------------------------------------------

function formatMonthYear(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

function formatDlExpiry(ymd: string | null): string | null {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

/** Build a comma+and joined English string of missing-doc display names.
 *  English-only by design — translators can rework if needed (see plan
 *  Task 3 Step 4 note re: avoiding localized "and"). */
function humanizeMissingDocs(keys: DocKey[]): string {
  const NAMES: Record<DocKey, string> = {
    photo: 'Photo',
    dl: 'DL',
    aadhaar: 'Aadhaar',
    pan: 'PAN',
  };
  const labels = keys.map(k => NAMES[k]);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  // 3+ → comma list with final "and"
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

// --- Doc cards -----------------------------------------------------------

type DocKey = 'photo' | 'dl' | 'aadhaar' | 'pan';

const UPLOADED_PILL_STYLE: CSSProperties = {
  background: 'var(--mint-soft)',
  color: 'var(--green-700)',
  border: '1px solid var(--mint-line)',
};

const MISSING_PILL_STYLE: CSSProperties = {
  background: 'var(--status-warning-bg,#FBF1DE)',
  color: 'var(--status-warning-fg,#8A5E1D)',
  border: '1px solid rgba(197, 142, 58, 0.35)',
};

const UPLOADED_TILE_STYLE: CSSProperties = {
  background: 'var(--mint-soft)',
  color: 'var(--green-700)',
};

const MISSING_TILE_STYLE: CSSProperties = {
  background: 'var(--status-warning-bg,#FBF1DE)',
  color: 'var(--status-warning-fg,#8A5E1D)',
};

function DocCardIcon({ docKey, uploaded }: { docKey: DocKey; uploaded: boolean }) {
  const style = uploaded ? UPLOADED_TILE_STYLE : MISSING_TILE_STYLE;
  return (
    <div
      className="flex items-center justify-center flex-shrink-0 rounded-[10px]"
      style={{ width: 44, height: 44, ...style }}
    >
      {docKey === 'photo' ? <UserIcon /> : <CardIcon />}
    </div>
  );
}

// --- Component -----------------------------------------------------------

export function ProfileRoute() {
  const { t } = useTranslation('profile');
  const navigate = useNavigate();
  const driver = useAuth(s => s.driver);
  const signOut = useAuth(s => s.signOut);
  const setDriver = (d: DriverProfile) => useAuth.setState({ driver: d });
  const themePref = useTheme(s => s.pref);
  const currentLang = currentLanguage();

  useSetPageHeader({ backTo: '/', title: t('profile') }, []);

  const [secondary, setSecondary] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSecondary('');
    setEditing(false);
  }, [driver?.id]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  // Auto-dismiss the "Saved" indicator after a short delay so it doesn't
  // sit between rows indefinitely (it has no other mount/edit reset).
  useEffect(() => {
    if (!saved) return;
    const tid = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(tid);
  }, [saved]);

  const onSavePhone = async () => {
    if (!driver) return;
    // Validate before hitting the server so the driver gets a clear reason
    // instead of a generic "Save failed" bounce. Empty clears the number.
    const digits = secondary.replace(/\D/g, '');
    if (digits.length > 0 && digits.length !== 10) {
      setError(t('account.secondaryPhoneInvalid', { defaultValue: 'Enter a 10-digit mobile number' }));
      return;
    }
    setSaving(true); setError(null); setSaved(false);
    try {
      const updated = await apiFetch<DriverProfile>('/driver-app/me', {
        method: 'PATCH',
        body: JSON.stringify({
          secondaryPhone: secondary ? `+91${secondary.replace(/\D/g, '')}` : null,
        }),
      });
      setDriver(updated);
      await invalidate('/me');
      setSaved(true);
      setEditing(false);
      setSecondary('');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!driver) return <div className="flex justify-center p-8"><Spinner size={32} /></div>;

  // ---- KYC status -------------------------------------------------------
  const docs: { key: DocKey; uploaded: boolean; targetPath: string }[] = [
    { key: 'photo'   as const,
      uploaded: driver.hasPhoto,
      targetPath: driver.hasPhoto   ? '/profile/kyc/photo/view'   : '/profile/kyc' },
    { key: 'dl'      as const,
      uploaded: driver.hasDl,
      targetPath: driver.hasDl      ? '/profile/kyc/dl/view'      : '/profile/kyc/dl' },
    { key: 'aadhaar' as const,
      uploaded: driver.hasAadhaar,
      targetPath: driver.hasAadhaar ? '/profile/kyc/aadhaar/view' : '/profile/kyc/aadhaar' },
    { key: 'pan'     as const,
      uploaded: driver.hasPan,
      targetPath: driver.hasPan     ? '/profile/kyc/pan/view'     : '/profile/kyc/pan' },
  ];
  const missingDocs = docs.filter(d => !d.uploaded);
  const showNudge = missingDocs.length > 0;

  // ---- Hero -------------------------------------------------------------
  const initials = (driver.name || 'D').trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const apiBase = env().VITE_API_BASE.replace(/\/+$/, '');
  const photoSrc = driver.hasPhoto ? `${apiBase}/v1/drivers/${driver.id}/photo` : null;

  // ---- Settings -------------------------------------------------------
  const langInfo = LANG_LIST.find(l => l.code === currentLang);
  const langLabel = langInfo?.native ?? 'English';
  const themeLabel =
    themePref === 'light' ? t('settings.themeLight') :
    themePref === 'dark'  ? t('settings.themeDark')  :
                            t('settings.themeSystem');

  // ---- Doc-card subtitle copy ------------------------------------------
  // Capture the narrowed (non-null) driver into a local so the helpers below
  // can read it without re-tripping the closure-narrowing limitation that
  // `function subtitleFor(...) { driver.x }` triggers under TS strict mode.
  const d: DriverProfile = driver;

  const subtitleFor = (key: DocKey, uploaded: boolean): string => {
    if (key === 'photo') {
      return uploaded ? t('kycDocs.photoUploaded') : t('kycDocs.photoMissing');
    }
    if (key === 'dl') {
      if (!uploaded) return t('kycDocs.dlMissing');
      const expiry = formatDlExpiry(d.drivingLicenseExpiry);
      if (d.drivingLicenseNumber && expiry) {
        return `${d.drivingLicenseNumber} · ${t('kycDocs.dlExpires', { date: expiry })}`;
      }
      return d.drivingLicenseNumber ?? t('kycDocs.dlMissing');
    }
    if (key === 'aadhaar') {
      if (!uploaded) return t('kycDocs.aadhaarMissing');
      return d.aadhaarNumber ? formatAadhaar(d.aadhaarNumber) : t('kycDocs.aadhaarMissing');
    }
    // pan
    if (!uploaded) return t('kycDocs.panMissing');
    return d.panNumber ?? t('kycDocs.panMissing');
  };

  const titleFor = (key: DocKey): string => {
    if (key === 'photo')   return t('kycDocs.photoTitle');
    if (key === 'dl')      return t('kyc.dlTitle');
    if (key === 'aadhaar') return t('kyc.aadhaarTitle');
    return t('kyc.panTitle');
  };

  return (
    <div className="px-5 pt-2 pb-10">
      {/* Hero — avatar (84px) + camera overlay, name, "Driver · Operator" */}
      <div className="text-center pt-3 pb-2 mb-4">
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div
            aria-hidden
            style={{
              width: 84,
              height: 84,
              borderRadius: 999,
              background: 'var(--mint-soft)',
              color: 'var(--green-700)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 34,
              fontWeight: 800,
              letterSpacing: '-0.025em',
              border: '3px solid var(--color-surface)',
              boxShadow: '0 2px 14px rgba(15, 59, 38, 0.10)',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {photoSrc ? (
              <img
                src={photoSrc}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  // Hide the broken image so the initials underneath show through.
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : null}
            {!photoSrc ? <span>{initials || 'D'}</span> : (
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: -1 }}>{initials || 'D'}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => { haptic.tap(); navigate('/profile/kyc'); }}
            aria-label={t('kycDocs.photoTitle')}
            style={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              width: 28,
              height: 28,
              borderRadius: 999,
              background: 'var(--color-btn-primary,#2E9E65)',
              color: 'white',
              border: '2px solid var(--color-surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <CameraIcon />
          </button>
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: '-0.025em',
            marginTop: 14,
            color: 'var(--color-text)',
          }}
        >
          {driver.name}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--color-text-secondary)',
            marginTop: 4,
          }}
        >
          {t('driverRole', { defaultValue: 'Driver' })}
          {driver.ownerOrgName ? (
            <>
              {' · '}
              <span className="serif-accent" style={{ fontSize: 14, color: 'var(--color-text)' }}>
                {driver.ownerOrgName}
              </span>
            </>
          ) : null}
        </div>
      </div>

      <InstallCard />               {/* NEW — second top-level child, between Hero and KYC nudge */}

      <PushStatusCard />

      {/* KYC nudge — amber variant */}
      {showNudge ? (
        <button
          type="button"
          onClick={() => { haptic.tap(); navigate('/profile/kyc'); }}
          className="w-full text-left mb-4 flex gap-3 items-center p-4 rounded-[14px]"
          style={{
            background: 'var(--status-warning-bg,#FBF1DE)',
            border: '1px solid rgba(197, 142, 58, 0.35)',
            borderLeft: '4px solid var(--status-warning-fg,#8A5E1D)',
          }}
        >
          <div
            className="flex items-center justify-center flex-shrink-0 rounded-[10px]"
            style={{ width: 40, height: 40, background: 'var(--status-warning-bg,#FBF1DE)', color: 'var(--status-warning-fg,#8A5E1D)' }}
          >
            <FileWarnIcon />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="text-[11px] font-bold uppercase tracking-wider"
              style={{ color: 'var(--status-warning-fg,#8A5E1D)' }}
            >
              {missingDocs.length === 1
                ? t('kycNudgeAmber.eyebrowOne')
                : t('kycNudgeAmber.eyebrowMany', { count: missingDocs.length })}
            </div>
            <div className="text-sm font-bold text-[var(--color-text)] mt-1">
              {missingDocs.length === 4
                ? t('kycNudgeAmber.bodyAll')
                : t('kycNudgeAmber.body', { docs: humanizeMissingDocs(missingDocs.map(d => d.key)) })}
            </div>
          </div>
          <ChevronRight />
        </button>
      ) : null}

      {/* Account section */}
      <h2
        className="text-[11px] font-bold uppercase tracking-wider mb-2 mt-2"
        style={{ color: 'var(--color-text-dim,#9AA3AC)' }}
      >
        {t('account.title')}
      </h2>
      <Card className="mb-4">
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Row 1: Phone (read-only) */}
          <div
            className="flex items-center justify-between"
            style={{ padding: '12px 0', borderBottom: '1px solid var(--color-border)' }}
          >
            <span className="text-sm text-[var(--color-text-secondary)]">{t('phone')}</span>
            <span className="text-sm font-semibold text-[var(--color-text)]">{driver.phone}</span>
          </div>

          {/* Row 2: Secondary phone (inline pencil edit) */}
          <div
            style={{ padding: '12px 0', borderBottom: '1px solid var(--color-border)' }}
          >
            {!editing ? (
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--color-text-secondary)]">{t('secondaryPhone')}</span>
                <div className="flex items-center gap-2">
                  {driver.secondaryPhone ? (
                    <span className="text-sm font-semibold text-[var(--color-text)]">{driver.secondaryPhone}</span>
                  ) : (
                    <span className="text-sm font-semibold" style={{ color: 'var(--color-primary-500,#2E9E65)' }}>
                      {t('account.secondaryPhoneAdd')}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => { haptic.tap(); setSaved(false); setEditing(true); setError(null); }}
                    aria-label={t('editSecondaryPhone', { defaultValue: 'Edit secondary phone' })}
                    className="inline-flex items-center justify-center text-[var(--color-primary-500,#2E9E65)] hover:text-[var(--color-primary-600,#268857)] rounded"
                    style={{ background: 'transparent', border: 0, padding: 4, margin: -4, width: 24, height: 24 }}
                  >
                    <PencilIcon />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-semibold text-[var(--color-text)] mb-2">
                  {t('secondaryPhone')}
                </label>
                <div className="flex items-stretch border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] overflow-hidden">
                  <div className="flex items-center gap-1 px-3 border-r border-[var(--color-border)] text-sm font-semibold">+91</div>
                  <input
                    ref={inputRef}
                    type="tel"
                    inputMode="numeric"
                    value={secondary}
                    onChange={e => setSecondary(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder={t('secondaryPhonePlaceholder')}
                    className="flex-1 border-none outline-none px-3 py-3 text-base bg-transparent"
                  />
                </div>
                {error ? <p className="text-sm text-[var(--color-danger)] mt-2">{error}</p> : null}
                <div className="mt-4 flex gap-2">
                  <Button onClick={() => { haptic.select(); void onSavePhone(); }} loading={saving}>
                    {t('save', { ns: 'common' })}
                  </Button>
                  <button
                    type="button"
                    onClick={() => { haptic.tap(); setEditing(false); setSecondary(''); setError(null); }}
                    className="text-sm font-semibold text-[var(--color-text-secondary)] px-3"
                  >
                    {t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
                  </button>
                </div>
              </div>
            )}
          </div>
          {saved && !editing ? <p className="text-sm text-[var(--color-primary-500,#2E9E65)] mt-2">{t('saved')}</p> : null}

          {/* Row 3: Driver since (read-only) */}
          <div
            className="flex items-center justify-between"
            style={{ padding: '12px 0' }}
          >
            <span className="text-sm text-[var(--color-text-secondary)]">{t('account.driverSince')}</span>
            <span className="text-sm font-semibold text-[var(--color-text)]">{formatMonthYear(driver.createdAt) ?? ''}</span>
          </div>
        </div>
      </Card>

      {/* KYC Documents section */}
      <h2
        className="text-[11px] font-bold uppercase tracking-wider mb-2 mt-2"
        style={{ color: 'var(--color-text-dim,#9AA3AC)' }}
      >
        {t('kycDocs.title')}
      </h2>
      <div className="space-y-2 mb-4">
        {docs.map(doc => {
          const cardStyle: CSSProperties = doc.uploaded
            ? {
                background: 'var(--mint-soft)',
                border: '1px solid var(--mint-line)',
              }
            : {
                background: 'var(--status-warning-bg,#FBF1DE)',
                border: '1px solid rgba(197, 142, 58, 0.35)',
                borderLeft: '4px solid var(--status-warning-fg,#8A5E1D)',
              };
          const baseSubtitle = subtitleFor(doc.key, doc.uploaded);
          const lastUpdatedAt =
            doc.key === 'photo'   ? d.photoUpdatedAt          :
            doc.key === 'dl'      ? d.drivingLicenseUpdatedAt :
            doc.key === 'aadhaar' ? d.aadhaarUpdatedAt        :
                                    d.panUpdatedAt;
          const lastUpdatedMonth = formatMonthYear(lastUpdatedAt);
          const lastUpdatedSubtitle = lastUpdatedMonth
            ? ` · ${t('kycDocs.lastUpdated', { date: lastUpdatedMonth })}`
            : '';
          const finalSubtitle = baseSubtitle + lastUpdatedSubtitle;
          return (
            <button
              key={doc.key}
              type="button"
              onClick={() => { haptic.tap(); navigate(doc.targetPath); }}
              className="w-full text-left rounded-[14px] p-4 flex items-start gap-3"
              style={cardStyle}
            >
              <DocCardIcon docKey={doc.key} uploaded={doc.uploaded} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-base font-bold text-[var(--color-text)]">{titleFor(doc.key)}</span>
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
                    style={doc.uploaded ? UPLOADED_PILL_STYLE : MISSING_PILL_STYLE}
                  >
                    <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
                    {doc.uploaded ? t('kyc.statusUploaded') : t('kyc.statusMissing')}
                  </span>
                </div>
                <div className="text-xs text-[var(--color-text-secondary)] truncate">
                  {finalSubtitle}
                </div>
                <div
                  className="text-xs font-bold mt-2 inline-flex items-center gap-1"
                  style={{ color: 'var(--green-700)' }}
                >
                  {doc.uploaded ? (
                    <>
                      {t('kycDocs.viewDocument')}
                      <span aria-hidden>›</span>
                    </>
                  ) : (
                    <>
                      {t('kycDocs.uploadNow')}
                      <ArrowUp />
                    </>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Settings section */}
      <h2
        className="text-[11px] font-bold uppercase tracking-wider mb-2 mt-2"
        style={{ color: 'var(--color-text-dim,#9AA3AC)' }}
      >
        {t('settings.title')}
      </h2>
      <Card className="mb-6">
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Row 1: Language & theme */}
          <button
            type="button"
            onClick={() => { haptic.tap(); navigate('/profile/language'); }}
            className="w-full flex items-center gap-3 text-left"
            style={{ padding: '12px 0', borderBottom: '1px solid var(--color-border)', background: 'transparent', border: 0, borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: 'var(--color-border)' }}
          >
            <div
              className="flex items-center justify-center flex-shrink-0 rounded-[10px]"
              style={{ width: 36, height: 36, background: 'var(--mint-soft)', color: 'var(--green-700)' }}
            >
              <GlobeIcon />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[var(--color-text)]">{t('settings.languageTheme')}</div>
              <div className="text-xs text-[var(--color-text-secondary)] truncate">
                {t('settings.languageThemeSubtitle', { lang: langLabel, theme: themeLabel })}
              </div>
            </div>
            <ChevronRight />
          </button>

          {/* Row 2: Help */}
          <button
            type="button"
            onClick={() => { haptic.tap(); navigate('/help'); }}
            className="w-full flex items-center gap-3 text-left"
            style={{ padding: '12px 0', background: 'transparent', border: 0 }}
          >
            <div
              className="flex items-center justify-center flex-shrink-0 rounded-[10px]"
              style={{ width: 36, height: 36, background: 'var(--mint-soft)', color: 'var(--green-700)' }}
            >
              <HelpIcon />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[var(--color-text)]">{t('settings.help')}</div>
              <div className="text-xs text-[var(--color-text-secondary)] truncate">
                {t('settings.helpSubtitle')}
              </div>
            </div>
            <ChevronRight />
          </button>
        </div>
      </Card>

      {/* Sign out */}
      <button
        type="button"
        onClick={() => { haptic.tap(); void signOut(); }}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-[14px] font-bold"
        style={{
          color: 'var(--color-danger,#C13834)',
          border: '1.5px solid var(--color-danger,#C13834)',
          background: 'transparent',
        }}
      >
        <SignOutIcon />
        {t('signOutAction')}
      </button>
    </div>
  );
}
